// Card-only checkout flow (auth + capture, with 3DS support).
//
// 1. Collect a card via Stripe `CardElement`.
// 2. `stripe.createPaymentMethod` → paymentMethodId.
// 3. Call `authorizeAndCapturePayment` Cloud Function:
//      • creates a PaymentIntent with capture_method: "manual", confirms it.
//      • returns either { success: true } (authorized + captured), or
//        { requiresAction: true, clientSecret } if 3DS is needed.
// 4. If 3DS, run `stripe.handleCardAction(clientSecret)` in the browser, then
//    call `capturePaymentIntent` to finalize.
// 5. Add the user to the event guest list and call onSuccess.
//
// Free events bypass Stripe entirely.
import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Icon,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { IoCardOutline, IoLockClosed, IoTicket } from "react-icons/io5";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebase-config";
import {
  addUserToGuestList,
  recordTicketPurchase,
} from "../../FBFunctions/memberFunctions";
import { getStripeMode, getKeyError } from "../../lib/stripeMode";

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: false,
  style: {
    base: {
      fontSize: "15px",
      color: "#1a202c",
      fontFamily: 'Inter, "Segoe UI", system-ui, sans-serif',
      "::placeholder": { color: "#a0aec0" },
      iconColor: "#508cf7",
    },
    invalid: {
      color: "#e53e3e",
      iconColor: "#e53e3e",
    },
  },
};

export const CardPay = ({ eventItem, price, userData, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);

  const stripeMode = getStripeMode();

  const showError = (description) =>
    toast({
      title: "Payment failed",
      description,
      status: "error",
      duration: 6000,
      isClosable: true,
    });

  // Free event — skip Stripe entirely.
  const handleFreeJoin = async () => {
    try {
      setSubmitting(true);
      await addUserToGuestList(eventItem.id, userData.id);
      toast({
        title: "Joined event",
        description: "You've successfully joined this free event.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onSuccess?.();
    } catch (err) {
      console.error("Free-join error:", err);
      showError("Failed to join the event. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardPay = async () => {
    if (!stripe || !elements) {
      showError("Payment system isn't ready yet, please try again.");
      return;
    }

    // Guard rail: refuse to tokenize a test card with a live publishable key
    // (and vice-versa). This is what produces the `live_mode_test_card`
    // decline if it slips through.
    const keyError = getKeyError();
    if (keyError) {
      showError(keyError);
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      showError("No card details found.");
      return;
    }

    setSubmitting(true);
    setCardError("");

    try {
      // 1) Tokenize the card.
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: {
          email: userData?.email,
          name: userData?.displayName,
          address: {
            country: "CA",
          },
        },
      });
      if (pmError) {
        setCardError(pmError.message || "Invalid card details.");
        showError(pmError.message || "Invalid card details.");
        return;
      }

      // 2) Server: auth + (auto-capture if no 3DS).
      // The cloud function reads the event, computes the venue
      // waterfall split, and routes the Stripe charge accordingly:
      //   • below-min  → 100% to venue owner's Connect account
      //   • above-min  → 30% producer / 70% platform
      //   • straddle   → venueShare to venue, producerShare via a
      //                  post-capture transfer to the producer
      // No `applicationFee` / `accountId` are passed here anymore —
      // the server is the source of truth for routing decisions.
      const stripeAmount = Math.round(price);
      console.log("[CardPay] requesting authorize", {
        eventId: eventItem.id,
        eventName: eventItem.name,
        amountCents: stripeAmount,
        mode: stripeMode,
      });
      const authorize = httpsCallable(functions, "authorizeAndCapturePayment");
      const authResp = await authorize({
        amount: stripeAmount, // already in cents from caller
        currency: "cad",
        customerId: userData.customerId,
        paymentMethodId: paymentMethod.id,
        email: userData.email,
        eventId: eventItem.id,
        mode: stripeMode,
      });
      const auth = authResp.data || {};
      // Track the PaymentIntent across both the auto-capture and 3DS
      // branches so we can attach it to the transactions doc later.
      let paymentIntentId = auth.paymentIntentId || null;
      // The server returns the exact split it routed in Stripe. We
      // forward this to `recordTicketPurchase` so the Firestore ledger
      // records exactly what Stripe distributed (rather than
      // recomputing from a possibly-stale running revenue).
      const serverSplit = auth.split || null;

      console.log("[CardPay] authorize response", {
        success: auth.success,
        requiresAction: auth.requiresAction,
        paymentIntentId,
        splitCase: serverSplit?.splitCase,
        venueShare: serverSplit?.venueShare,
        producerShare: serverSplit?.producerShare,
        platformShare: serverSplit?.platformShare,
        plannedTransfers: serverSplit?.transfers,
        executedTransfers: auth.transferResults,
        reconciliation: auth.reconciliation,
      });

      // Surface ANY split transfer failures — the buyer's charge
      // succeeded but a recipient's share may not have moved. We
      // don't roll the purchase back, but we shout in the console
      // so the operator can reconcile.
      const failedTransfers = (auth.transferResults || []).filter(
        (t) => t && t.error
      );
      if (failedTransfers.length > 0) {
        console.error(
          "❌ [CardPay] split transfers failed — money may be stuck on the platform:",
          failedTransfers
        );
      }

      // 3) 3DS challenge path.
      if (auth.requiresAction && auth.clientSecret) {
        const { error: actionError, paymentIntent } = await stripe.handleCardAction(
          auth.clientSecret
        );
        if (actionError) {
          showError(actionError.message || "3-D Secure authentication failed.");
          return;
        }
        if (paymentIntent) paymentIntentId = paymentIntent.id || paymentIntentId;
        if (paymentIntent && paymentIntent.status === "requires_capture") {
          const capture = httpsCallable(functions, "capturePaymentIntent");
          const capResp = await capture({
            paymentIntentId: paymentIntent.id,
            mode: stripeMode,
          });
          if (!capResp.data?.success) {
            showError("We couldn't capture your payment after verification.");
            return;
          }
          paymentIntentId =
            capResp.data?.paymentIntentId || paymentIntentId;
          console.log("[CardPay] capture response (3DS)", {
            paymentIntentId,
            executedTransfers: capResp.data?.transferResults,
            reconciliation: capResp.data?.reconciliation,
          });
        } else if (!paymentIntent || paymentIntent.status !== "succeeded") {
          showError("Card authentication didn't complete. Please try again.");
          return;
        }
      } else if (!auth.success) {
        // Non-3DS failure path.
        showError("Your card was declined or could not be processed.");
        return;
      }

      // 4) Record the purchase: writes the `transactions` doc,
      //    increments per-event revenue counters, adds the buyer to
      //    `members`, and creates the ticket subdoc — all atomically.
      //    `serverSplit` ensures the ledger matches what Stripe did.
      await recordTicketPurchase({
        eventId: eventItem.id,
        buyerUid: userData.id,
        paidAmount: Math.round(price),
        paymentIntentId,
        stripeMode,
        currency: "cad",
        serverSplit,
      });
      toast({
        title: "Payment successful",
        description: "You're on the list — see you at the event!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onSuccess?.();
    } catch (err) {
      console.error("Card pay error:", err);
      showError(err.message || "Something went wrong processing your payment.");
    } finally {
      setSubmitting(false);
    }
  };

  // Free event flow.
  if (price === 0) {
    return (
      <Button
        onClick={handleFreeJoin}
        isLoading={submitting}
        leftIcon={<Icon as={IoTicket} />}
        size="md"
        mt={4}
        w="100%"
        bg="#508cf7"
        color="white"
        fontSize="14px"
        _hover={{ bg: "#226cf1" }}
        borderRadius="12px"
      >
        Join for Free
      </Button>
    );
  }

  // Paid flow.
  if (!stripe || !elements) {
    return (
      <Center my={3}>
        <CircularProgress isIndeterminate color="blue.500" size="22px" />
      </Center>
    );
  }

  return (
    <VStack spacing={3} align="stretch" mt={2}>
      <Box>
        <Text fontWeight="700" fontSize="12px" mb={2} color="gray.600">
          <Icon as={IoCardOutline} mr={1} mb="-2px" /> Card details
          <Text as="span" color="gray.400" fontWeight="600">
            {" "}
            · Canada
          </Text>
        </Text>
        <Box
          borderWidth="1px"
          borderColor={cardError ? "red.300" : "gray.200"}
          borderRadius="12px"
          px={3}
          py={3}
          bg="white"
          transition="border-color 0.15s"
          _focusWithin={{ borderColor: "#508cf7", boxShadow: "0 0 0 1px #508cf7" }}
        >
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={(e) => {
              setCardComplete(e.complete);
              setCardError(e.error?.message || "");
            }}
          />
        </Box>
        {cardError && (
          <Text fontSize="12px" color="red.500" mt={1}>
            {cardError}
          </Text>
        )}
      </Box>

      <Button
        onClick={handleCardPay}
        isLoading={submitting}
        loadingText="Processing"
        isDisabled={!cardComplete || submitting}
        size="md"
        w="100%"
        bg="#508cf7"
        color="white"
        fontSize="14px"
        _hover={{ bg: "#226cf1" }}
        _disabled={{ bg: "gray.300", cursor: "not-allowed", _hover: { bg: "gray.300" } }}
        borderRadius="12px"
      >
        Pay & Reserve
      </Button>

      <Text fontSize="11px" color="gray.400" textAlign="center">
        <Icon as={IoLockClosed} mb="-2px" mr={1} />
        Secured by Stripe
        {stripeMode === "test" && " · TEST MODE"}
      </Text>
    </VStack>
  );
};
